import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Badge, Row, Col, Card, CardBody, Spinner } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import * as signalR from "@microsoft/signalr";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { API_WEB_URLS } from "../../constants/constAPI";

// Map center adjustment component
const ChangeMapBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    try {
      const validPoints = points.filter(p => Array.isArray(p) && !isNaN(p[0]) && !isNaN(p[1]) && p[0] !== 0 && p[1] !== 0);
      if (validPoints.length === 1) {
        map.setView(validPoints[0], 14);
      } else if (validPoints.length > 1) {
        const bounds = L.latLngBounds(validPoints);
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }
    } catch (e) {
      console.warn("Error fitting map bounds:", e);
    }
  }, [points, map]);
  return null;
};

// Custom Marker Icons
const createPinIcon = (color, label, iconClass) => {
  return L.divIcon({
    html: `
      <div style="position: relative; display: flex; flex-direction: column; align-items: center; transform: translate(-50%, -100%);">
        <div style="
          background-color: ${color};
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 10px rgba(0,0,0,0.35);
          border: 2px solid #ffffff;
        ">
          <div style="transform: rotate(45deg); font-size: 13px; font-weight: bold;">
            ${iconClass ? `<i class="${iconClass}"></i>` : label}
          </div>
        </div>
        <div style="
          width: 8px;
          height: 8px;
          background: rgba(0,0,0,0.25);
          border-radius: 50%;
          margin-top: 2px;
        "></div>
      </div>
    `,
    className: "custom-map-pin",
    iconSize: [32, 42],
    iconAnchor: [16, 42],
  });
};

const getDriverVehicleIcon = (isLive = true) => {
  return L.divIcon({
    html: `
      <div style="position: relative; width: 44px; height: 44px; display: flex; align-items: center; justify-content: center; transform: translate(-50%, -50%);">
        ${isLive ? `
          <div style="
            position: absolute;
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background-color: #2563eb;
            opacity: 0.35;
            animation: pulseRadar 1.6s infinite ease-out;
          "></div>
        ` : ''}
        <div style="
          width: 34px;
          height: 34px;
          background-color: #1e40af;
          border: 2.5px solid #ffffff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(30, 64, 175, 0.5);
          z-index: 10;
        ">
          <i class="fas fa-truck"></i>
        </div>
      </div>
    `,
    className: "custom-driver-vehicle-icon",
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });
};

const LiveTripTrackingModal = ({ isOpen, toggle, booking, onClose }) => {
  const [driverPos, setDriverPos] = useState(null);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(booking?.Status || booking?.StatusName || "Active Ride");
  const [roadPolyline, setRoadPolyline] = useState([]);
  const connectionRef = useRef(null);

  const bookingId = booking?.Id || booking?.BookingId;

  // Pickup Coords
  const pickupCoords = useMemo(() => {
    const lat = parseFloat(booking?.PickupLatitude || booking?.PickupLat);
    const lng = parseFloat(booking?.PickupLongitude || booking?.PickupLng);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      return [lat, lng];
    }
    return null;
  }, [booking]);

  // Drop Coords
  const dropCoords = useMemo(() => {
    const lat = parseFloat(booking?.Stop1Latitude || booking?.DropLatitude || booking?.Stop1Lat);
    const lng = parseFloat(booking?.Stop1Longitude || booking?.DropLongitude || booking?.Stop1Lng);
    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      return [lat, lng];
    }
    return null;
  }, [booking]);

  // Intermediate Stop Coords
  const stop2Coords = useMemo(() => {
    const lat = parseFloat(booking?.Stop2Latitude);
    const lng = parseFloat(booking?.Stop2Longitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [booking]);

  const stop3Coords = useMemo(() => {
    const lat = parseFloat(booking?.Stop3Latitude);
    const lng = parseFloat(booking?.Stop3Longitude);
    return (!isNaN(lat) && !isNaN(lng) && lat !== 0) ? [lat, lng] : null;
  }, [booking]);

  // Initial driver coords from booking record (prioritizing newly assigned driver live coords)
  useEffect(() => {
    if (booking) {
      const initLat = parseFloat(
        booking.DriverCurrentLatitude || booking.DriverLatitude || booking.CurrentLatitude
      );
      const initLng = parseFloat(
        booking.DriverCurrentLongitude || booking.DriverLongitude || booking.CurrentLongitude
      );
      if (!isNaN(initLat) && !isNaN(initLng) && initLat !== 0 && initLng !== 0) {
        setDriverPos([initLat, initLng]);
        setLastUpdatedTime(new Date().toLocaleTimeString());
      } else if (pickupCoords) {
        setDriverPos(pickupCoords);
      }
      setBookingStatus(booking.Status || booking.StatusName || "Active Ride");
    }
  }, [booking, pickupCoords]);

  // Fetch freshest booking data from server on open and poll as a reliable fallback
  useEffect(() => {
    if (!isOpen || !bookingId) return;

    let isMounted = true;
    const fetchLatestBookingData = async () => {
      try {
        const baseUrl = API_WEB_URLS.BASE || "https://apikirayago.shinewellsofttech.co.in/api/V1/";
        const res = await axios.get(`${baseUrl}Masters/0/token/BookingMaster/Id/${bookingId}`);
        const list = res?.data?.data?.dataList;
        if (Array.isArray(list) && list.length > 0 && isMounted) {
          const fresh = list[0];
          const freshLat = parseFloat(
            fresh.DriverCurrentLatitude || fresh.DriverLatitude || fresh.CurrentLatitude
          );
          const freshLng = parseFloat(
            fresh.DriverCurrentLongitude || fresh.DriverLongitude || fresh.CurrentLongitude
          );
          if (!isNaN(freshLat) && !isNaN(freshLng) && freshLat !== 0 && freshLng !== 0) {
            setDriverPos([freshLat, freshLng]);
            setLastUpdatedTime(new Date().toLocaleTimeString());
          }
          if (fresh.Status || fresh.StatusName) {
            setBookingStatus(fresh.Status || fresh.StatusName);
          }
        }
      } catch (e) {
        // Silent error handling for background fetch
      }
    };

    fetchLatestBookingData();
    const pollInterval = setInterval(fetchLatestBookingData, 5000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
    };
  }, [isOpen, bookingId]);

  // Connect to SignalR BookingHub for this specific booking
  useEffect(() => {
    if (!isOpen || !bookingId) return;

    const hubUrl = API_WEB_URLS.BASE
      ? API_WEB_URLS.BASE.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub")
      : "https://apikirayago.shinewellsofttech.co.in/bookingHub";

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    connection.on("DriverLocationUpdated", (data) => {
      const incomingBookingId = data.bookingId || data.BookingId;
      if (String(incomingBookingId) === String(bookingId)) {
        const assignedDriverId = booking?.DriverMemberId || booking?.DriverId || booking?.F_MemberMaster;
        const incomingDriverId = data.driverId || data.DriverId;
        if (incomingDriverId && assignedDriverId && String(incomingDriverId) !== String(assignedDriverId)) {
          console.warn(`[LiveTrackModal] Ignored location update from unassigned driver ${incomingDriverId} (assigned: ${assignedDriverId})`);
          return;
        }

        const lat = parseFloat(data.latitude || data.Latitude);
        const lng = parseFloat(data.longitude || data.Longitude);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          setDriverPos([lat, lng]);
          setLastUpdatedTime(new Date().toLocaleTimeString());
        }
      }
    });

    connection.on("DriverLiveLocationUpdated", (data) => {
      const assignedDriverId = booking?.DriverMemberId || booking?.DriverId || booking?.F_MemberMaster;
      const incomingDriverId = data.driverId || data.DriverId;
      const incomingBookingId = data.bookingId || data.BookingId;

      const isForAssignedDriver = assignedDriverId && incomingDriverId && String(assignedDriverId) === String(incomingDriverId);
      const isForThisBooking = incomingBookingId && String(incomingBookingId) === String(bookingId);

      if (isForAssignedDriver || (isForThisBooking && (!assignedDriverId || !incomingDriverId || String(assignedDriverId) === String(incomingDriverId)))) {
        const lat = parseFloat(data.latitude || data.Latitude);
        const lng = parseFloat(data.longitude || data.Longitude);
        if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
          setDriverPos([lat, lng]);
          setLastUpdatedTime(new Date().toLocaleTimeString());
        }
      }
    });

    connection.on("BookingReassigned", (data) => {
      if (String(data.bookingId || data.BookingId) === String(bookingId)) {
        console.log(`[LiveTrackModal] Booking ${bookingId} was reassigned:`, data);
      }
    });

    connection.on("BookingUpdated", (updatedId) => {
      if (String(updatedId) === String(bookingId)) {
        console.log(`[LiveTrackModal] Booking ${bookingId} updated status`);
      }
    });

    connection.start()
      .then(() => {
        setIsLiveConnected(true);
        connection.invoke("JoinBookingGroup", String(bookingId)).catch(console.warn);
        connection.invoke("RegisterAdmin").catch(console.warn);
      })
      .catch((err) => {
        console.warn("[LiveTrackModal] SignalR connect error:", err);
        setIsLiveConnected(false);
      });

    connectionRef.current = connection;

    return () => {
      if (connectionRef.current) {
        connectionRef.current.invoke("LeaveBookingGroup", String(bookingId)).catch(() => {});
        connectionRef.current.stop().catch(() => {});
      }
    };
  }, [isOpen, bookingId, booking]);

  // Route path without driver position (the static journey)
  const routePolyline = useMemo(() => {
    const list = [];
    if (pickupCoords) list.push(pickupCoords);
    if (stop2Coords) list.push(stop2Coords);
    if (stop3Coords) list.push(stop3Coords);
    if (dropCoords) list.push(dropCoords);
    return list;
  }, [pickupCoords, stop2Coords, stop3Coords, dropCoords]);

  // Fetch real road route geometry via OSRM
  useEffect(() => {
    let isCancelled = false;
    const fetchRoadGeometry = async () => {
      if (!pickupCoords || !dropCoords) {
        setRoadPolyline([]);
        return;
      }

      const waypoints = [
        `${pickupCoords[1]},${pickupCoords[0]}`,
        ...(stop2Coords ? [`${stop2Coords[1]},${stop2Coords[0]}`] : []),
        ...(stop3Coords ? [`${stop3Coords[1]},${stop3Coords[0]}`] : []),
        `${dropCoords[1]},${dropCoords[0]}`
      ].join(';');

      try {
        const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${waypoints}?overview=full&geometries=geojson`);
        const json = await res.json();
        if (!isCancelled && json && json.code === 'Ok' && json.routes && json.routes.length > 0) {
          const coords = json.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]);
          setRoadPolyline(coords);
          return;
        }
      } catch (err) {
        console.warn("OSRM road route fetch error, falling back to direct points:", err);
      }

      if (!isCancelled) {
        const fallback = [
          pickupCoords,
          ...(stop2Coords ? [stop2Coords] : []),
          ...(stop3Coords ? [stop3Coords] : []),
          dropCoords
        ];
        setRoadPolyline(fallback);
      }
    };

    if (isOpen) {
      fetchRoadGeometry();
    }

    return () => {
      isCancelled = true;
    };
  }, [isOpen, pickupCoords, stop2Coords, stop3Coords, dropCoords]);

  // Compile all points for bounding box & polyline
  const allRoutePoints = useMemo(() => {
    const list = [];
    if (roadPolyline.length > 0) {
      list.push(...roadPolyline);
    } else {
      if (pickupCoords) list.push(pickupCoords);
      if (stop2Coords) list.push(stop2Coords);
      if (stop3Coords) list.push(stop3Coords);
      if (dropCoords) list.push(dropCoords);
    }
    if (driverPos) list.push(driverPos);
    return list;
  }, [roadPolyline, pickupCoords, stop2Coords, stop3Coords, dropCoords, driverPos]);

  const defaultCenter = driverPos || pickupCoords || [26.2389, 73.0243];

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="modal-fullscreen-lg-down">
      <style>
        {`
          @keyframes pulseRadar {
            0% { transform: scale(0.6); opacity: 0.9; }
            100% { transform: scale(2.2); opacity: 0; }
          }
        `}
      </style>
      <ModalHeader toggle={toggle} className="bg-dark text-white d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <i className="fas fa-satellite-dish text-success font-size-18 me-1"></i>
          <span>Live Ride Tracking: <b>#{bookingId}</b></span>
          <Badge color={isLiveConnected ? "success" : "warning"} className="ms-2 py-1 px-2">
            {isLiveConnected ? "● Live SignalR Stream Active" : "Connecting..."}
          </Badge>
        </div>
      </ModalHeader>

      <ModalBody className="p-0 bg-light">
        <Row className="g-0">
          {/* Map Section (Left/Center 7 cols) */}
          <Col lg={8} md={7} style={{ height: "540px", position: "relative" }}>
            <MapContainer
              center={defaultCenter}
              zoom={13}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />

              <ChangeMapBounds points={allRoutePoints} />

              {/* Pickup Marker */}
              {pickupCoords && (
                <Marker position={pickupCoords} icon={createPinIcon("#16a34a", "P", "fas fa-map-pin")}>
                  <Popup>
                    <div className="p-1">
                      <b className="text-success">🟢 Pickup Point</b>
                      <div className="text-muted small mt-1">{booking?.PickupAddress || "Pickup Location"}</div>
                      {booking?.SenderName && <div className="small mt-1">Sender: <b>{booking.SenderName}</b></div>}
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Intermediate Stops */}
              {stop2Coords && (
                <Marker position={stop2Coords} icon={createPinIcon("#f59e0b", "S2", "fas fa-store")}>
                  <Popup>
                    <div className="p-1">
                      <b className="text-warning">Stop 2</b>
                      <div className="text-muted small mt-1">{booking?.Stop2Address || "Stop 2"}</div>
                    </div>
                  </Popup>
                </Marker>
              )}

              {stop3Coords && (
                <Marker position={stop3Coords} icon={createPinIcon("#f59e0b", "S3", "fas fa-store")}>
                  <Popup>
                    <div className="p-1">
                      <b className="text-warning">Stop 3</b>
                      <div className="text-muted small mt-1">{booking?.Stop3Address || "Stop 3"}</div>
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Drop Marker */}
              {dropCoords && (
                <Marker position={dropCoords} icon={createPinIcon("#dc2626", "D", "fas fa-flag-checkered")}>
                  <Popup>
                    <div className="p-1">
                      <b className="text-danger">🏁 Final Destination</b>
                      <div className="text-muted small mt-1">{booking?.Stop1Address || booking?.DropAddress || "Drop Location"}</div>
                      {booking?.Stop1ReceiverName && <div className="small mt-1">Receiver: <b>{booking.Stop1ReceiverName}</b></div>}
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Live Driver Marker */}
              {driverPos && (
                <Marker position={driverPos} icon={getDriverVehicleIcon(isLiveConnected)}>
                  <Popup>
                    <div className="p-1">
                      <b className="text-primary">🚚 Live Driver Position</b>
                      <div className="small mt-1">Driver: <b>{booking?.DriverName || "Assigned Driver"}</b></div>
                      <div className="small">Plate: <b>{booking?.VehicleNo || "Vehicle"}</b></div>
                      <div className="text-muted small mt-1">Last Signal: {lastUpdatedTime || "Just now"}</div>
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Planned Route Polyline Casing & Main Path */}
              {(roadPolyline.length > 1 || routePolyline.length > 1) && (
                <>
                  <Polyline
                    positions={roadPolyline.length > 1 ? roadPolyline : routePolyline}
                    pathOptions={{ color: "#1e3a8a", weight: 8, opacity: 0.6 }}
                  />
                  <Polyline
                    positions={roadPolyline.length > 1 ? roadPolyline : routePolyline}
                    pathOptions={{ color: "#2563eb", weight: 5, opacity: 0.95 }}
                  />
                </>
              )}

              {/* Driver to Drop Live Polyline */}
              {driverPos && dropCoords && (
                <>
                  <Polyline
                    positions={[driverPos, dropCoords]}
                    pathOptions={{ color: "#065f46", weight: 6, opacity: 0.5, dashArray: "6, 8" }}
                  />
                  <Polyline
                    positions={[driverPos, dropCoords]}
                    pathOptions={{ color: "#10b981", weight: 4, opacity: 0.95, dashArray: "6, 8" }}
                  />
                </>
              )}
            </MapContainer>

            {/* Map Overlay Badge */}
            <div style={{ position: "absolute", top: 12, right: 12, zIndex: 1000 }}>
              <div className="bg-white shadow rounded-pill px-3 py-1.5 d-flex align-items-center gap-2 border">
                <span className="spinner-grow spinner-grow-sm text-success" style={{ width: 8, height: 8 }}></span>
                <span className="small fw-semibold text-dark">
                  {lastUpdatedTime ? `Updated at ${lastUpdatedTime}` : "Connecting GPS..."}
                </span>
              </div>
            </div>
          </Col>

          {/* Telemetry & Details Panel (Right 5 cols) */}
          <Col lg={4} md={5} className="p-3 bg-white border-start" style={{ maxHeight: "540px", overflowY: "auto" }}>
            {/* Status & Fare */}
            <div className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom">
              <div>
                <span className="text-muted small d-block">TRIP STATUS</span>
                <Badge color="primary" className="font-size-12 px-2 py-1 mt-1">
                  {bookingStatus}
                </Badge>
              </div>
              <div className="text-end">
                <span className="text-muted small d-block">TOTAL FARE</span>
                <span className="fw-bold text-success font-size-18">
                  ₹{(booking?.Price || booking?.FinalAmountCustomer || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Driver Profile */}
            <Card className="border mb-3 shadow-none bg-light">
              <CardBody className="p-2.5">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="avatar-xs bg-primary text-white rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-user font-size-12"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="text-muted small d-block">ASSIGNED DRIVER</span>
                    <strong className="text-dark">{booking?.DriverName || "Unassigned"}</strong>
                  </div>
                  {booking?.DriverMobile && (
                    <a href={`tel:${booking.DriverMobile}`} className="btn btn-sm btn-success rounded-pill px-2 py-1">
                      <i className="fas fa-phone-alt font-size-11 me-1"></i> Call
                    </a>
                  )}
                </div>
                <div className="row g-2 small text-muted">
                  <div className="col-6">
                    Vehicle: <b className="text-dark">{booking?.VehicleNo || "-"}</b>
                  </div>
                  <div className="col-6">
                    Model: <b className="text-dark">{booking?.VehicleName || booking?.VehicleCategory || "-"}</b>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Customer Details */}
            <Card className="border mb-3 shadow-none bg-light">
              <CardBody className="p-2.5">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div className="avatar-xs bg-success text-white rounded-circle d-flex align-items-center justify-content-center">
                    <i className="fas fa-user-check font-size-12"></i>
                  </div>
                  <div className="flex-grow-1">
                    <span className="text-muted small d-block">CUSTOMER</span>
                    <strong className="text-dark">{booking?.CustomerName || booking?.UserName || booking?.SenderName || "Customer"}</strong>
                  </div>
                  {(booking?.CustomerMobile || booking?.UserMobileNo || booking?.SenderMobile) && (
                    <a href={`tel:${booking?.CustomerMobile || booking?.UserMobileNo || booking?.SenderMobile}`} className="btn btn-sm btn-outline-primary rounded-pill px-2 py-1">
                      <i className="fas fa-phone-alt font-size-11 me-1"></i> Call
                    </a>
                  )}
                </div>
                <div className="small text-muted">
                  Mobile: <b className="text-dark">{booking?.CustomerMobile || booking?.UserMobileNo || booking?.SenderMobile || "-"}</b>
                </div>
              </CardBody>
            </Card>

            {/* OTP Verification Info */}
            <div className="d-flex gap-2 mb-3">
              <div className="flex-fill p-2 bg-light rounded border text-center">
                <small className="text-muted d-block">START OTP</small>
                <span className="font-monospace fw-bold text-dark font-size-14">
                  {booking?.OTP || "----"}
                </span>
              </div>
              <div className="flex-fill p-2 bg-light rounded border text-center">
                <small className="text-muted d-block">DELIVERY OTP</small>
                <span className="font-monospace fw-bold text-dark font-size-14">
                  {booking?.DeliveryOTP || "----"}
                </span>
              </div>
            </div>

            {/* Route Timeline */}
            <div className="border-top pt-2">
              <span className="text-muted small fw-bold d-block mb-2">TRIP ROUTE</span>
              <div className="position-relative ps-3" style={{ borderLeft: "2px solid #e2e8f0", marginLeft: "6px" }}>
                {/* Pickup */}
                <div className="mb-2 position-relative">
                  <span style={{ position: "absolute", left: "-19px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#16a34a" }}></span>
                  <small className="text-muted d-block font-size-11">PICKUP</small>
                  <span className="small text-dark fw-semibold text-wrap">{booking?.PickupAddress || "-"}</span>
                </div>

                {/* Drop */}
                <div className="position-relative">
                  <span style={{ position: "absolute", left: "-19px", top: "2px", width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#dc2626" }}></span>
                  <small className="text-muted d-block font-size-11">DROP</small>
                  <span className="small text-dark fw-semibold text-wrap">{booking?.Stop1Address || booking?.DropAddress || "-"}</span>
                </div>
              </div>
            </div>

            {/* Live GPS Telemetry */}
            {driverPos && (
              <div className="mt-3 p-2 bg-light rounded border text-muted small">
                <div className="d-flex justify-content-between">
                  <span>GPS Lat: <b>{driverPos[0].toFixed(5)}</b></span>
                  <span>Lng: <b>{driverPos[1].toFixed(5)}</b></span>
                </div>
                <div className="text-end font-size-11 mt-1 text-success">
                  <i className="fas fa-check-circle me-1"></i> SignalR streaming active
                </div>
              </div>
            )}
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="bg-light py-2">
        <Button color="secondary" size="sm" onClick={toggle}>
          Close Tracking
        </Button>
      </ModalFooter>
    </Modal>
  );
};

LiveTripTrackingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  booking: PropTypes.object,
  onClose: PropTypes.func,
};

export default LiveTripTrackingModal;
